import type { RuleSet } from './contracts';

/**
 * Rulesets are looked up by id, never imported directly by the server, the
 * renderer or the persistence layer. That indirection is the whole point: a new
 * ruleset ships by registering itself here, and nothing downstream changes.
 *
 * A match row stores `rule_set_id` + `rule_set_version`, so an old match is
 * always replayable against the rules it was actually played under.
 */

// The registry is intentionally type-erased. Callers that know the concrete
// state type re-apply it at the call site; callers that do not (the room, the
// persistence layer) never touch the state's shape anyway.
type ErasedRuleSet = RuleSet<never, never>;

const registry = new Map<string, ErasedRuleSet>();

export function registerRuleSet<TState, TConfig>(ruleSet: RuleSet<TState, TConfig>): void {
  if (registry.has(ruleSet.id)) {
    throw new Error(`RuleSet "${ruleSet.id}" is already registered.`);
  }
  registry.set(ruleSet.id, ruleSet as unknown as ErasedRuleSet);
}

export function hasRuleSet(id: string): boolean {
  return registry.has(id);
}

export function getRuleSet<TState = unknown, TConfig = unknown>(
  id: string,
): RuleSet<TState, TConfig> {
  const found = registry.get(id);
  if (!found) {
    throw new Error(
      `Unknown RuleSet "${id}". Registered: ${[...registry.keys()].join(', ') || '(none)'}`,
    );
  }
  return found as unknown as RuleSet<TState, TConfig>;
}

export function listRuleSets(): { id: string; version: number }[] {
  return [...registry.values()].map((r) => ({ id: r.id, version: r.version }));
}

/** Test-only. Never call from application code. */
export function clearRuleSetRegistry(): void {
  registry.clear();
}
